import { useState } from 'preact/hooks'
import type { MeasurementSessionRecord } from '~/models/measurementSession'
import { NAVY_FORMULA_SITES, SITE_LABELS } from '~/models/measurementSite'
import type { LengthDisplayUnit } from '~/models/settings'
import type { MeasurementSessionId } from '~/types/ids'
import type { Centimetres } from '~/types/units'
import { formatCalendarDate, formatLength } from '~/utils/format'
import { Button } from '../primitives/Button'
import styles from './MeasurementHistory.module.css'

/**
 * Saved measurement sessions, newest first, each removable.
 *
 * Sessions are keyed per save rather than per date, so measuring twice in one
 * day produces two records. That is deliberate — a second reading is real data
 * and overwriting it silently would discard something the user recorded — but
 * it means the user needs a way to remove the one they did not mean to keep.
 *
 * Sessions sharing a date are marked as such. Without that, two rows differing
 * only by a timestamp read as a bug rather than as two measurements.
 *
 * Deletion is always explicit and confirmed in place. There is no undo, so the
 * confirmation says what will be lost rather than asking "are you sure?".
 */

interface MeasurementHistoryProps {
  sessions: readonly MeasurementSessionRecord[]
  lengthUnit: LengthDisplayUnit
  onDelete: (id: MeasurementSessionId) => void
  /** How many of the most recent sessions to list. */
  limit?: number
  busyId?: MeasurementSessionId | undefined
}

const DEFAULT_LIMIT = 10

export function MeasurementHistory({
  sessions,
  lengthUnit,
  onDelete,
  limit = DEFAULT_LIMIT,
  busyId,
}: MeasurementHistoryProps) {
  const [confirming, setConfirming] = useState<MeasurementSessionId | undefined>(undefined)

  if (sessions.length === 0) return null

  // Newest first. `recordedAt` breaks ties within a date, which is exactly the
  // case this list exists to make legible.
  const ordered = [...sessions].sort((left, right) => {
    const byDate = right.date.localeCompare(left.date)
    return byDate !== 0 ? byDate : right.recordedAt.localeCompare(left.recordedAt)
  })

  const perDate = new Map<string, number>()
  for (const session of ordered) {
    perDate.set(session.date, (perDate.get(session.date) ?? 0) + 1)
  }

  const shown = ordered.slice(0, limit)

  return (
    <section class={styles.card} aria-labelledby="history-heading">
      <h2 id="history-heading" class={styles.heading}>
        Saved measurements
      </h2>

      <ul class={styles.list}>
        {shown.map((session) => {
          const sameDate = perDate.get(session.date) ?? 1
          const waist = session.sites[NAVY_FORMULA_SITES.waist]
          const neck = session.sites[NAVY_FORMULA_SITES.neck]

          return (
            <li key={session.id} class={styles.item}>
              <div class={styles.summary}>
                <p class={styles.date}>
                  {formatCalendarDate(session.date)}
                  <span class={styles.time}>
                    {' · '}
                    {new Date(session.recordedAt).toLocaleTimeString(undefined, {
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </span>
                </p>

                {sameDate > 1 ? (
                  <p class={styles.duplicate}>
                    {sameDate} measurements saved on this date. Keep the one you trust and remove
                    the rest.
                  </p>
                ) : null}

                <dl class={styles.detail}>
                  {waist === undefined ? null : (
                    <div class={styles.row}>
                      <dt>{SITE_LABELS[NAVY_FORMULA_SITES.waist]}</dt>
                      <dd>{formatLength(waist.medianCm as Centimetres, lengthUnit)}</dd>
                    </div>
                  )}
                  {neck === undefined ? null : (
                    <div class={styles.row}>
                      <dt>{SITE_LABELS[NAVY_FORMULA_SITES.neck]}</dt>
                      <dd>{formatLength(neck.medianCm as Centimetres, lengthUnit)}</dd>
                    </div>
                  )}
                  {session.derived.bodyFatPercent === undefined ? null : (
                    <div class={styles.row}>
                      <dt>Estimate</dt>
                      <dd>{session.derived.bodyFatPercent.toFixed(1)}%</dd>
                    </div>
                  )}
                </dl>
              </div>

              {confirming === session.id ? (
                <div class={styles.confirm}>
                  <p class={styles.confirmText}>
                    Delete this measurement? Its readings and its estimate are removed from Project
                    Sola. This cannot be undone.
                  </p>
                  <div class={styles.actions}>
                    <Button variant="secondary" onClick={() => setConfirming(undefined)}>
                      Keep it
                    </Button>
                    <Button
                      busy={busyId === session.id}
                      busyLabel="Deleting…"
                      onClick={() => {
                        setConfirming(undefined)
                        onDelete(session.id)
                      }}
                    >
                      Delete
                    </Button>
                  </div>
                </div>
              ) : (
                <Button
                  variant="secondary"
                  onClick={() => setConfirming(session.id)}
                  label={`Delete the measurement from ${formatCalendarDate(session.date)}`}
                >
                  Delete
                </Button>
              )}
            </li>
          )
        })}
      </ul>

      {/* Never silently truncate: if some sessions are not listed, say so. */}
      {ordered.length > shown.length ? (
        <p class={styles.note}>
          Showing the {shown.length} most recent of {ordered.length} saved measurements.
        </p>
      ) : null}
    </section>
  )
}
